import {
	JobReview,
	JobReviewThesis,
	ReviewDocumentFormat,
} from 'modules/contestacao/pages/review/contestacao-review.model';
import {
	ContestacaoTemplate,
	ContestacaoTemplateConfig,
	ContestacaoTemplatePreviewDTO,
} from 'services/contestacao-template/contestacao-template.model';

/**
 * Documento final da contestação (GET /jobs/:id/review/document), gerado no navegador
 * a partir das teses aprovadas: PDF de verdade (texto simples), DOCX mínimo válido ou HTML.
 */

interface DocumentBlock {
	kind: 'title' | 'heading' | 'paragraph';
	text: string;
}

/** O que o documento lê da revisão; a pré-visualização da config monta isto a partir do template. */
interface DocumentSource {
	case: JobReview['case'];
	topics: { name: string; theses: Pick<JobReviewThesis, 'name' | 'content' | 'reviewStatus'>[] }[];
}

const PREVIEW_CASE = '0001234-56.2026.5.02.0001';
const PREVIEW_LOREM =
	'<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>';

function htmlToParagraphs(html: string | null | undefined): string[] {
	if (!html) return [];
	return html
		.split(/<\/p>|<br\s*\/?>/i)
		.map((chunk) =>
			chunk
				.replace(/<[^>]+>/g, '')
				.replace(/&nbsp;/g, ' ')
				.replace(/&amp;/g, '&')
				.replace(/\s+/g, ' ')
				.trim(),
		)
		.filter(Boolean);
}

function buildBlocks(
	review: DocumentSource,
	config: ContestacaoTemplateConfig | null,
): DocumentBlock[] {
	const blocks: DocumentBlock[] = [];
	const paragraph = (text: string) => blocks.push({ kind: 'paragraph', text });

	htmlToParagraphs(config?.addressing).forEach(paragraph);
	// `processNumber` é rich text com `{{case}}`; `null` ⇒ a linha não entra no documento (como no Hub)
	htmlToParagraphs(
		config
			? config.processNumber?.replace(/\{\{\s*case\s*\}\}/g, review.case)
			: `Processo nº ${review.case}`,
	).forEach(paragraph);
	htmlToParagraphs(config?.presentation).forEach(paragraph);
	if (config?.opening) {
		blocks.push({ kind: 'title', text: config.opening.title });
		htmlToParagraphs(config.opening.body).forEach(paragraph);
	}

	let topicIndex = 0;
	for (const topic of review.topics) {
		const approved = topic.theses.filter((thesis) => thesis.reviewStatus === 'APPROVED');
		if (approved.length === 0) continue;
		topicIndex += 1;
		blocks.push({ kind: 'heading', text: `${topicIndex}. ${topic.name.toUpperCase()}` });
		approved.forEach((thesis, thesisIndex) => {
			blocks.push({ kind: 'heading', text: `${topicIndex}.${thesisIndex + 1}. ${thesis.name}` });
			htmlToParagraphs(thesis.content).forEach(paragraph);
		});
	}

	const signatures = config?.signatures;
	if (signatures) {
		htmlToParagraphs(signatures.closing).forEach(paragraph);
		const today = new Date().toLocaleDateString('pt-BR', {
			day: '2-digit',
			month: 'long',
			year: 'numeric',
		});
		paragraph(`${signatures.place ?? ''}, ${today}.`);
		for (const signatory of signatures.signatories) {
			const detail = htmlToParagraphs(signatory.detail).join(' ');
			paragraph(detail ? `${signatory.name} — ${detail}` : signatory.name);
		}
	}
	return blocks;
}

// ── HTML ─────────────────────────────────────────────────────────────────────

function escapeXml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function toHtml(blocks: DocumentBlock[]): Blob {
	const body = blocks
		.map((block) => {
			const tag = block.kind === 'title' ? 'h1' : block.kind === 'heading' ? 'h2' : 'p';
			return `<${tag}>${escapeXml(block.text)}</${tag}>`;
		})
		.join('\n');
	const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Contestação</title><style>body{font-family:'Times New Roman',serif;max-width:720px;margin:40px auto;text-align:justify;line-height:1.5}h1{text-align:center}h2{font-size:1em}</style></head><body>${body}</body></html>`;
	return new Blob([html], { type: 'text/html' });
}

// ── PDF (Helvetica, WinAnsi) ─────────────────────────────────────────────────

const WIN_ANSI_EXTRAS: Record<string, number> = {
	'—': 0x97,
	'–': 0x96,
	'“': 0x93,
	'”': 0x94,
	'‘': 0x91,
	'’': 0x92,
	'…': 0x85,
	'•': 0x95,
};

function pdfString(text: string): string {
	let result = '';
	for (const char of text) {
		const code = WIN_ANSI_EXTRAS[char] ?? char.charCodeAt(0);
		const byte = code <= 0xff ? code : 0x3f;
		const safe =
			byte === 0x28 || byte === 0x29 || byte === 0x5c
				? `\\${String.fromCharCode(byte)}`
				: String.fromCharCode(byte);
		result += safe;
	}
	return result;
}

function wrap(text: string, maxChars: number): string[] {
	const lines: string[] = [];
	let line = '';
	for (const word of text.split(' ')) {
		if (line && line.length + word.length + 1 > maxChars) {
			lines.push(line);
			line = word;
		} else line = line ? `${line} ${word}` : word;
	}
	if (line) lines.push(line);
	return lines;
}

function toPdf(blocks: DocumentBlock[]): Blob {
	const pageHeight = 842;
	const top = 780;
	const bottom = 70;
	const pages: string[][] = [[]];
	let y = top;

	const write = (text: string, font: 'F1' | 'F2', size: number, x: number) => {
		if (y < bottom) {
			pages.push([]);
			y = top;
		}
		pages[pages.length - 1].push(`BT /${font} ${size} Tf ${x} ${y} Td (${pdfString(text)}) Tj ET`);
		y -= size + 5;
	};

	for (const block of blocks) {
		if (block.kind === 'title') {
			y -= 8;
			write(block.text, 'F2', 15, 250);
			y -= 8;
		} else if (block.kind === 'heading') {
			y -= 6;
			wrap(block.text, 80).forEach((line) => write(line, 'F2', 11, 60));
		} else {
			wrap(block.text, 95).forEach((line) => write(line, 'F1', 10, 60));
			y -= 6;
		}
	}

	const objects: string[] = [];
	const pageIds = pages.map((_, index) => 5 + index * 2);
	objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
	objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;
	objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
	objects[4] =
		'<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';
	pages.forEach((commands, index) => {
		const stream = commands.join('\n');
		objects[5 + index * 2] =
			`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 ${pageHeight}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${6 + index * 2} 0 R >>`;
		objects[6 + index * 2] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
	});

	let output = '%PDF-1.4\n';
	const offsets: number[] = [];
	for (let id = 1; id < objects.length; id++) {
		offsets[id] = output.length;
		output += `${id} 0 obj\n${objects[id]}\nendobj\n`;
	}
	const xref = output.length;
	output += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
	for (let id = 1; id < objects.length; id++)
		output += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
	output += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

	// Todo caractere já está em 0..255: cada um vira exatamente um byte
	const bytes = Uint8Array.from(output, (char) => char.charCodeAt(0));
	return new Blob([bytes], { type: 'application/pdf' });
}

// ── DOCX (ZIP sem compressão) ────────────────────────────────────────────────

const CRC_TABLE = Array.from({ length: 256 }, (_, index) => {
	let crc = index;
	for (let bit = 0; bit < 8; bit++) crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
	return crc >>> 0;
});

function crc32(bytes: Uint8Array): number {
	let crc = 0xffffffff;
	for (const byte of bytes) crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
	return (crc ^ 0xffffffff) >>> 0;
}

function zip(files: { name: string; content: string }[]): Uint8Array<ArrayBuffer> {
	const encoder = new TextEncoder();
	const chunks: Uint8Array[] = [];
	const central: Uint8Array[] = [];
	let offset = 0;

	for (const file of files) {
		const name = encoder.encode(file.name);
		const data = encoder.encode(file.content);
		const crc = crc32(data);

		const local = new DataView(new ArrayBuffer(30));
		local.setUint32(0, 0x04034b50, true);
		local.setUint16(4, 20, true);
		local.setUint32(14, crc, true);
		local.setUint32(18, data.length, true);
		local.setUint32(22, data.length, true);
		local.setUint16(26, name.length, true);
		chunks.push(new Uint8Array(local.buffer), name, data);

		const entry = new DataView(new ArrayBuffer(46));
		entry.setUint32(0, 0x02014b50, true);
		entry.setUint16(4, 20, true);
		entry.setUint16(6, 20, true);
		entry.setUint32(16, crc, true);
		entry.setUint32(20, data.length, true);
		entry.setUint32(24, data.length, true);
		entry.setUint16(28, name.length, true);
		entry.setUint32(42, offset, true);
		central.push(new Uint8Array(entry.buffer), name);

		offset += 30 + name.length + data.length;
	}

	const centralSize = central.reduce((sum, chunk) => sum + chunk.length, 0);
	const end = new DataView(new ArrayBuffer(22));
	end.setUint32(0, 0x06054b50, true);
	end.setUint16(8, files.length, true);
	end.setUint16(10, files.length, true);
	end.setUint32(12, centralSize, true);
	end.setUint32(16, offset, true);

	const all = [...chunks, ...central, new Uint8Array(end.buffer)];
	const result = new Uint8Array(all.reduce((sum, chunk) => sum + chunk.length, 0));
	let position = 0;
	for (const chunk of all) {
		result.set(chunk, position);
		position += chunk.length;
	}
	return result;
}

function toDocx(blocks: DocumentBlock[]): Blob {
	const paragraphs = blocks
		.map((block) => {
			const bold = block.kind !== 'paragraph' ? '<w:b/>' : '';
			const size = block.kind === 'title' ? '<w:sz w:val="32"/>' : '<w:sz w:val="24"/>';
			const align = block.kind === 'title' ? 'center' : 'both';
			const indent = block.kind === 'paragraph' ? '<w:ind w:firstLine="709"/>' : '';
			return (
				`<w:p><w:pPr><w:jc w:val="${align}"/>${indent}<w:spacing w:after="200" w:line="360" w:lineRule="auto"/></w:pPr>` +
				`<w:r><w:rPr><w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>${bold}${size}</w:rPr>` +
				`<w:t xml:space="preserve">${escapeXml(block.text)}</w:t></w:r></w:p>`
			);
		})
		.join('');

	const bytes = zip([
		{
			name: '[Content_Types].xml',
			content:
				'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>',
		},
		{
			name: '_rels/.rels',
			content:
				'<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>',
		},
		{
			name: 'word/document.xml',
			content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${paragraphs}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1701" w:right="1247" w:bottom="1417" w:left="1247" w:header="709" w:footer="709" w:gutter="0"/></w:sectPr></w:body></w:document>`,
		},
	]);
	return new Blob([bytes], {
		type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	});
}

/**
 * Pré-visualização da config (POST /…/:id/preview): as teses do template, ou texto de exemplo,
 * com a config enviada no lugar da salva.
 */
export function buildPreviewDocument(
	template: ContestacaoTemplate,
	dto: ContestacaoTemplatePreviewDTO,
): Blob {
	const source: DocumentSource = {
		case: PREVIEW_CASE,
		topics: [...template.topics]
			.sort((first, second) => first.order - second.order)
			.map((topic) => ({
				name: topic.name,
				theses: [...topic.theses]
					.sort((first, second) => first.order - second.order)
					.map((thesis) => ({
						name: thesis.name,
						content: dto.content === 'template' ? thesis.content : PREVIEW_LOREM,
						reviewStatus: 'APPROVED' as const,
					})),
			})),
	};
	return toPdf(buildBlocks(source, dto.config ?? template.config));
}

export function buildReviewDocument(
	review: JobReview,
	config: ContestacaoTemplateConfig | null,
	format: ReviewDocumentFormat,
): Blob {
	const blocks = buildBlocks(review, config);
	if (format === 'pdf') return toPdf(blocks);
	if (format === 'docx') return toDocx(blocks);
	return toHtml(blocks);
}
